import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";
import OtpInput from "./OtpInput";

const CODE_LENGTH = 4;
const RESEND_INTERVAL_MS = 30 * 1000;

function pickErrorMessage(data, fallback) {
  if (!data) return fallback;
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (data.errors && typeof data.errors === "object") {
    for (const v of Object.values(data.errors)) {
      const arr = Array.isArray(v) ? v : [v];
      const first = arr.find((x) => typeof x === "string" && x.trim());
      if (first) return first;
    }
  }
  return fallback;
}

function ChangeEmailModalForm({ currentEmail = "", onSuccess }) {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [resendUntilMs, setResendUntilMs] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [resending, setResending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!resendUntilMs) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((resendUntilMs - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resendUntilMs]);

  useEffect(() => {
    if (!message) return;
    if (message.status) return;
    const t1 = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(t1);
  }, [message]);

  const startResendCooldown = useCallback(() => {
    setResendUntilMs(Date.now() + RESEND_INTERVAL_MS);
  }, []);

  const emailSchema = Yup.object({
    email: Yup.string()
      .email(t("authModal.login.errors.emailInvalid"))
      .required(t("authModal.login.errors.emailRequired"))
      .notOneOf(
        currentEmail ? [currentEmail] : [],
        t("profile.changeEmail.errors.sameEmail"),
      ),
  });

  const requestUpdate = async (targetEmail, { setSubmitting: setFormikSubmitting } = {}) => {
    const locale = getSecondApiLocale(i18n.language);
    setMessage(null);
    try {
      const { data } = await SecondApi.post(
        `/api/${locale}/auth/updateEmail`,
        { email: targetEmail },
      );
      const okText =
        data && typeof data.message === "string" && data.message.trim()
          ? data.message
          : t("profile.changeEmail.codeSent");
      setMessage({ status: true, text: okText });
      setNewEmail(targetEmail);
      setStep(2);
      startResendCooldown();
    } catch (error) {
      const fallback =
        error.code === "ERR_NETWORK" || !error.response
          ? t("authModal.login.errors.network")
          : t("profile.changeEmail.errors.requestGeneric");
      setMessage({
        status: false,
        text: pickErrorMessage(error.response?.data, fallback),
      });
    } finally {
      setFormikSubmitting?.(false);
    }
  };

  const handleStep1 = (values, helpers) => requestUpdate(values.email.trim(), helpers);

  const handleResend = async () => {
    if (secondsLeft > 0 || resending) return;
    setResending(true);
    await requestUpdate(newEmail);
    setResending(false);
  };

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    if (verificationCode.length !== CODE_LENGTH) {
      setMessage({
        status: false,
        text: t("authModal.verify.errors.incomplete"),
      });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    const locale = getSecondApiLocale(i18n.language);
    try {
      const { data } = await SecondApi.post(
        `/api/${locale}/auth/verifyUpdateEmail`,
        { verification_code: verificationCode },
      );
      const ok =
        data?.isSuccess === true ||
        data?.success === true ||
        (typeof data?.message === "string" &&
          /verified|success|updated|changed/i.test(data.message));
      if (ok) {
        setMessage({
          status: true,
          text:
            (data && typeof data.message === "string" && data.message.trim()) ||
            t("profile.changeEmail.success"),
        });
        if (typeof onSuccess === "function") {
          setTimeout(() => onSuccess(newEmail), 700);
        }
      } else {
        setMessage({
          status: false,
          text: pickErrorMessage(data, t("authModal.verify.errors.generic")),
        });
      }
    } catch (error) {
      const fallback =
        error.code === "ERR_NETWORK" || !error.response
          ? t("authModal.verify.errors.network")
          : t("authModal.verify.errors.generic");
      setMessage({
        status: false,
        text: pickErrorMessage(error.response?.data, fallback),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 1) {
    return (
      <Formik
        initialValues={{ email: "" }}
        validationSchema={emailSchema}
        onSubmit={handleStep1}
      >
        {({ isSubmitting }) => (
          <Form className="row gy-4">
            <div className="col-12">
              <p className="font-14 mb-1 text-body">
                {t("profile.changeEmail.step1.intro")}
              </p>
              {currentEmail && (
                <p className="font-14 mb-0 text-body">
                  {t("profile.changeEmail.currentEmail")}:{" "}
                  <span className="fw-600">{currentEmail}</span>
                </p>
              )}
            </div>

            {message && (
              <div
                className={`col-12 font-14 ${
                  message.status ? "text-success" : "text-danger"
                }`}
                role="status"
              >
                {message.text}
              </div>
            )}

            <div className="col-12">
              <label className="form-label mb-2 font-18 font-heading fw-600">
                {t("profile.changeEmail.step1.newEmailLabel")}
              </label>
              <div className="position-relative">
                <Field
                  type="email"
                  name="email"
                  autoComplete="email"
                  className="common-input common-input--withIcon"
                  placeholder={t("profile.changeEmail.step1.placeholder")}
                />
                <span className="input-icon">
                  <img src="../assets/images/icons/envelope-icon.svg" alt="" />
                </span>
              </div>
              <ErrorMessage
                name="email"
                component="div"
                className="text-danger mt-1 font-14"
              />
            </div>

            <div className="col-12">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-main btn-lg w-100 pill"
              >
                {isSubmitting
                  ? t("profile.changeEmail.step1.btnLoading")
                  : t("profile.changeEmail.step1.btn")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    );
  }

  // step === 2 — verify code
  const canResend = secondsLeft === 0 && !resending;
  return (
    <form onSubmit={handleVerify} className="row gy-4">
      <div className="col-12">
        <button
          type="button"
          onClick={() => {
            setStep(1);
            setVerificationCode("");
            setMessage(null);
          }}
          className="link text-main fw-500 bg-transparent border-0 p-0 font-14 d-inline-flex align-items-center gap-1"
        >
          <i className="las la-arrow-left" />
          {t("profile.changeEmail.step2.changeEmail")}
        </button>
      </div>

      <div className="col-12">
        <p className="font-14 mb-0 text-body">
          {t("profile.changeEmail.step2.intro", { email: newEmail })}
        </p>
      </div>

      {message && (
        <div
          className={`col-12 font-14 ${
            message.status ? "text-success" : "text-danger"
          }`}
          role="status"
        >
          {message.text}
        </div>
      )}

      <div className="col-12">
        <label className="form-label mb-2 font-18 font-heading fw-600">
          {t("authModal.verify.codeLabel")}
        </label>
        <OtpInput
          length={CODE_LENGTH}
          value={verificationCode}
          onChange={setVerificationCode}
          disabled={submitting}
        />
      </div>

      <div className="col-12">
        <button
          type="submit"
          disabled={submitting || verificationCode.length !== CODE_LENGTH}
          className="btn btn-main btn-lg w-100 pill"
        >
          {submitting
            ? t("profile.changeEmail.step2.btnLoading")
            : t("profile.changeEmail.step2.btn")}
        </button>
      </div>

      <div className="col-12 mb-0">
        <p className="text font-14 mb-0 text-center">
          {canResend ? (
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="link text-main text-decoration-underline fw-500 bg-transparent border-0 p-0 font-14"
            >
              {resending
                ? t("authModal.verify.resending")
                : t("authModal.verify.resendBtn")}
            </button>
          ) : (
            <span className="text-body">
              {t("authModal.verify.resendIn", { seconds: secondsLeft })}
            </span>
          )}
        </p>
      </div>
    </form>
  );
}

export default ChangeEmailModalForm;
